import { DOC_TYPES } from "../types";
import type { KbAdminEditorSectionProps } from "../kbAdminEditorProps";

type Props = Pick<
  KbAdminEditorSectionProps,
  | "publishedArticleLocked"
  | "loadedArticle"
  | "broaderScopeLocked"
  | "articleTitle"
  | "setArticleTitle"
  | "defaultArticleTitle"
  | "aiBusy"
  | "articleContent"
  | "aiPurpose"
  | "runAiHelp"
  | "articleAudience"
  | "setArticleAudience"
  | "articleStatus"
  | "setArticleStatus"
  | "canPublishKb"
  | "articleScope"
  | "setArticleScope"
  | "canManageManufacturerScope"
  | "canManageAppScope"
  | "articleDocType"
  | "setArticleDocType"
  | "articleTagsInput"
  | "setArticleTagsInput"
  | "articleSourceUrl"
  | "setArticleSourceUrl"
>;

export function KbEditorTopFields(props: Props) {
  const {
    publishedArticleLocked,
    loadedArticle,
    broaderScopeLocked,
    articleTitle,
    setArticleTitle,
    defaultArticleTitle,
    aiBusy,
    articleContent,
    aiPurpose,
    runAiHelp,
    articleAudience,
    setArticleAudience,
    articleStatus,
    setArticleStatus,
    canPublishKb,
    articleScope,
    setArticleScope,
    canManageManufacturerScope,
    canManageAppScope,
    articleDocType,
    setArticleDocType,
    articleTagsInput,
    setArticleTagsInput,
    articleSourceUrl,
    setArticleSourceUrl,
  } = props;

  return (
    <>
      {publishedArticleLocked ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
          {loadedArticle?.scope === "org"
            ? "This org article is live. Use “Submit for review” to send your edits to admins; they appear in Change proposals until approved."
            : "This article is already published. Trainer-level access cannot overwrite manufacturer or app-level live content."}
        </div>
      ) : null}

      {broaderScopeLocked ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
          This is a {loadedArticle?.scope ?? "shared"} document. Only platform admin or platform dev users can edit it directly.
        </div>
      ) : null}

      <div>
        <label className="mb-1 block text-xs text-gray-600 dark:text-zinc-300">Title</label>
        <div className="flex flex-wrap gap-2">
          <input
            className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
            value={articleTitle}
            onChange={(event) => setArticleTitle(event.target.value)}
            placeholder={defaultArticleTitle}
          />
          <button
            type="button"
            className="w-full sm:w-auto shrink-0 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900 disabled:opacity-50 dark:border-amber-600 dark:bg-amber-950/45 dark:text-amber-100 dark:hover:bg-amber-900/50"
            disabled={aiBusy || (!articleContent.trim() && !aiPurpose.trim())}
            onClick={() => void runAiHelp("title_only")}
            title="Suggest title from body or purpose (Title Case + org name when appropriate)"
          >
            {aiBusy ? "…" : "AI title"}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 px-3 py-3 space-y-2 dark:border-indigo-800 dark:bg-indigo-950/35">
        <div className="text-xs font-semibold text-indigo-950 dark:text-indigo-100">Who can see this & how it’s classified</div>
        <p className="text-[11px] text-indigo-900 leading-relaxed dark:text-indigo-200">
          Set <strong>audience</strong>, <strong>scope</strong> (who owns the article in the KB), and <strong>document type</strong> before saving.
          {!canManageManufacturerScope ? (
            <>
              {" "}
              Your account can only publish <strong>org</strong> articles for the active organization.
            </>
          ) : null}
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900 disabled:opacity-50 dark:border-amber-600 dark:bg-amber-950/45 dark:text-amber-100 dark:hover:bg-amber-900/50"
            disabled={aiBusy || (!articleContent.trim() && !aiPurpose.trim())}
            onClick={() => void runAiHelp("metadata_only")}
          >
            {aiBusy ? "Processing…" : "AI fill permissions & type"}
          </button>
        </div>
        <div className="grid gap-3 md:grid-cols-2 pt-1">
          <div>
            <label className="mb-1 block text-xs text-gray-600 dark:text-zinc-300">Audience</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
              value={articleAudience}
              onChange={(event) => setArticleAudience(event.target.value as any)}
            >
              <option value="internal">internal</option>
              <option value="customer">customer</option>
              <option value="both">both</option>
              <option value="any">any</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-600 dark:text-zinc-300">Status</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
              value={articleStatus}
              onChange={(event) => setArticleStatus(event.target.value as any)}
            >
              <option value="draft">draft</option>
              <option value="published" disabled={!canPublishKb}>
                published
              </option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-600 dark:text-zinc-300">Scope</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
              value={articleScope}
              onChange={(event) => setArticleScope(event.target.value as any)}
            >
              <option value="manufacturer" disabled={!canManageManufacturerScope}>
                manufacturer
              </option>
              <option value="app" disabled={!canManageAppScope}>
                app
              </option>
              <option value="org">org</option>
            </select>
            <div className="mt-1 text-[11px] text-gray-500 dark:text-zinc-400">
              {canManageManufacturerScope
                ? "Use org for company-specific content. Use manufacturer for shared manufacturer baseline docs. App scope is platform-managed."
                : "Your account can only save org-scoped articles for the active organization."}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-600 dark:text-zinc-300">Document type</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
              value={articleDocType}
              onChange={(event) => setArticleDocType(event.target.value)}
            >
              <option value="">(none)</option>
              {DOC_TYPES.map((docType) => (
                <option key={docType} value={docType}>
                  {docType}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs text-gray-600 dark:text-zinc-300">Tags</label>
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
            value={articleTagsInput}
            onChange={(event) => setArticleTagsInput(event.target.value)}
            placeholder="pricing, warranty, delivery"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs text-gray-600 dark:text-zinc-300">Source URL</label>
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
            value={articleSourceUrl}
            onChange={(event) => setArticleSourceUrl(event.target.value)}
            placeholder="https://example.com/reference"
          />
        </div>
      </div>
    </>
  );
}
